import * as yup from 'yup';
import { FIELD_DEFINITIONS, FIELD_TEXT_MESSAGES, MINOR_AGE } from '../../../config';
import subtractYears from '../../../utils/subtractYears';

export const HouseholdMemberSchema = yup.object().shape({
  firstName: yup
    .string()
    .required(FIELD_TEXT_MESSAGES.FIRST_NAME_REQUIRED)
    .min(FIELD_DEFINITIONS.FIRST_NAME_MIN_LENGTH, FIELD_TEXT_MESSAGES.FIRST_NAME_MIN_LENGTH)
    .max(FIELD_DEFINITIONS.FIRST_NAME_MAX_LENGTH, FIELD_TEXT_MESSAGES.FIRST_NAME_MAX_LENGTH)
    .matches(FIELD_DEFINITIONS.ONLY_LETTERS_ACCENTS, FIELD_TEXT_MESSAGES.ONLY_LETTERS_ACCENTS),
  middleName: yup
    .string()
    .optional()
    .transform((value) => (value === '' ? undefined : value))
    .max(FIELD_DEFINITIONS.MIDDLE_NAME_MAX_LENGTH, FIELD_TEXT_MESSAGES.MIDDLE_NAME_MAX_LENGTH)
    .test('letters-accents', FIELD_TEXT_MESSAGES.ONLY_LETTERS_ACCENTS, (value) => {
      if (!value) return true;
      return FIELD_DEFINITIONS.ONLY_LETTERS_ACCENTS.test(value);
    }),
  relationship: yup
    .object()
    .required(FIELD_TEXT_MESSAGES.RELATIONSHIP_REQUIRED)
    .shape({
      label: yup.string().required(),
      value: yup
        .string()
        .required(FIELD_TEXT_MESSAGES.RELATIONSHIP_REQUIRED)
        .test('not-empty', FIELD_TEXT_MESSAGES.RELATIONSHIP_REQUIRED, (val) => val !== ''),
    }),
  lastName: yup
    .string()
    .required(FIELD_TEXT_MESSAGES.LAST_NAME_REQUIRED)
    .min(FIELD_DEFINITIONS.LAST_NAME_MIN_LENGTH, FIELD_TEXT_MESSAGES.LAST_NAME_MIN_LENGTH)
    .max(FIELD_DEFINITIONS.LAST_NAME_MAX_LENGTH, FIELD_TEXT_MESSAGES.LAST_NAME_MAX_LENGTH)
    .matches(FIELD_DEFINITIONS.ONLY_LETTERS_ACCENTS, FIELD_TEXT_MESSAGES.ONLY_LETTERS_ACCENTS),
  birthdate: yup.string().when('$mode', {
    is: 'edit',
    then: (schema) =>
      schema
        .required(FIELD_TEXT_MESSAGES.BIRTHDATE_REQUIRED)
        .test('', FIELD_TEXT_MESSAGES.BIRTHDATE_MAX_DATE, (value) => {
          return value ? new Date(value).getTime() <= new Date().getTime() : true;
        })
        .test('', FIELD_TEXT_MESSAGES.BIRTHDATE_MINOR_MIN_DATE, (value) => {
          if (!value) return true;
          const minorDate = subtractYears(MINOR_AGE);
          return new Date(value).getTime() >= minorDate.getTime();
        }),
    otherwise: (schema) =>
      schema
        .required(FIELD_TEXT_MESSAGES.DOB_REQUIRED)
        .test('', FIELD_TEXT_MESSAGES.BIRTHDATE_MAX_DATE, (value) => {
          return value ? new Date(value).getTime() <= new Date().getTime() : true;
        })
        .test('', FIELD_TEXT_MESSAGES.BIRTHDATE_FLOOR_DATE, (value) => {
          return value ? new Date(value).getTime() >= new Date('1900-01-01').getTime() : true;
        }),
  }),
  sex: yup
    .object()
    .required(FIELD_TEXT_MESSAGES.SEX_REQUIRED)
    .shape({
      label: yup.string().required(),
      value: yup.string().required(FIELD_TEXT_MESSAGES.SEX_REQUIRED),
    }),
  street: yup
    .string()
    .required(FIELD_TEXT_MESSAGES.STREET_REQUIRED)
    .min(FIELD_DEFINITIONS.STREET_MIN_LENGTH, FIELD_TEXT_MESSAGES.STREET_MIN_LENGTH)
    .max(FIELD_DEFINITIONS.STREET_MAX_LENGTH, FIELD_TEXT_MESSAGES.STREET_MAX_LENGTH),
  unit: yup
    .string()
    .optional()
    .max(FIELD_DEFINITIONS.UNIT_MAX_LENGTH, FIELD_TEXT_MESSAGES.UNIT_MAX_LENGTH)
    .matches(
      FIELD_DEFINITIONS.ONLY_LETTERS_NUMBERS_ACCENTS,
      FIELD_TEXT_MESSAGES.ONLY_LETTERS_NUMBERS_ACCENTS,
    )
    .transform((value) => (value === '' ? undefined : value)),
  city: yup
    .string()
    .required(FIELD_TEXT_MESSAGES.CITY_REQUIRED)
    .min(FIELD_DEFINITIONS.CITY_MIN_LENGTH, FIELD_TEXT_MESSAGES.CITY_MIN_LENGTH)
    .max(FIELD_DEFINITIONS.CITY_MAX_LENGTH, FIELD_TEXT_MESSAGES.CITY_MAX_LENGTH)
    .matches(FIELD_DEFINITIONS.ONLY_LETTERS_ACCENTS, FIELD_TEXT_MESSAGES.ONLY_LETTERS_ACCENTS),
  state: yup
    .object()
    .required(FIELD_TEXT_MESSAGES.PATIENT_STATE_REQUIRED)
    .shape({
      label: yup.string().required(),
      value: yup.string().required(FIELD_TEXT_MESSAGES.PATIENT_STATE_REQUIRED),
    }),
  zip: yup
    .string()
    .required(FIELD_TEXT_MESSAGES.ZIP_REQUIRED)
    .min(FIELD_DEFINITIONS.ZIP_MIN_LENGTH, FIELD_TEXT_MESSAGES.ZIP_MIN_LENGTH)
    .max(FIELD_DEFINITIONS.ZIP_MAX_LENGTH, FIELD_TEXT_MESSAGES.ZIP_MAX_LENGTH)
    .matches(
      FIELD_DEFINITIONS.ONLY_LETTERS_NUMBERS_ACCENTS,
      FIELD_TEXT_MESSAGES.ONLY_LETTERS_NUMBERS_ACCENTS,
    ),
  country: yup
    .object()
    .required(FIELD_TEXT_MESSAGES.COUNTRY_REQUIRED)
    .shape({
      label: yup.string().required(),
      value: yup.string().required(FIELD_TEXT_MESSAGES.COUNTRY_REQUIRED),
    }),
  email: yup.string().when(['$isAdult', '$mode'], {
    is: (isAdult: any, mode: any) => isAdult && mode !== 'edit',
    then: (schema) =>
      schema
        .required(FIELD_TEXT_MESSAGES.EMAIL_REQUIRED)
        .min(FIELD_DEFINITIONS.EMAIL_MIN_LENGTH, FIELD_TEXT_MESSAGES.EMAIL_MIN_LENGTH)
        .max(FIELD_DEFINITIONS.EMAIL_MAX_LENGTH, FIELD_TEXT_MESSAGES.EMAIL_MAX_LENGTH)
        .email(FIELD_TEXT_MESSAGES.EMAIL_INVALID),
  }),
  phone: yup.string().when(['$isAdult', '$mode'], {
    is: (isAdult: any, mode: any) => isAdult && mode !== 'edit',
    then: (schema) =>
      schema
        .required(FIELD_TEXT_MESSAGES.PHONE_REQUIRED)
        .test(
          'verifyIfStringIsAPhoneNumber',
          FIELD_TEXT_MESSAGES.FIELD_NUMBER_ONLY,
          (fieldValue) => !isNaN(+(fieldValue ? fieldValue.replace(/[^\d]/g, '') : '')),
        )
        .test(
          'minLength',
          FIELD_TEXT_MESSAGES.PHONE_MIN_LENGTH,
          (val) => !((val?.length || 0) < FIELD_DEFINITIONS.PHONE_MIN_LENGTH),
        )
        .test(
          'maxLength',
          FIELD_TEXT_MESSAGES.PHONE_MAX_LENGTH,
          (val) => !((val?.length || 0) > FIELD_DEFINITIONS.PHONE_MAX_LENGTH),
        )
        .test('containsNonNumbers', FIELD_TEXT_MESSAGES.FIELD_NUMBER_ONLY, (fieldValue) => {
          return !fieldValue || !fieldValue.match(/[^\d]/g);
        }),
  }),
});
